import { apiClient } from './client'
import { ApiError } from './errors'
import { getTokens } from 'utils/jwtStorage'

export interface TwoFaStatus {
  enrolled: boolean
  legacyOtpEnabled: boolean
  enrolledAt?: string
}

export interface TwoFaAssociation {
  secretCode: string
  qrCodeUri: string
}

// The serverless auth router is mounted under /auth and the TOTP endpoints take
// the Cognito access token in the body (used for the Cognito SDK calls); the
// Bearer header (added by the axios interceptor) authenticates the request. The
// { status, data } envelope is unwrapped by the interceptor, so response.data is
// the inner payload directly.
function accessToken(): string {
  return getTokens()?.accessToken ?? ''
}

const ISSUER = 'Goldie'

export async function associateSoftwareToken(): Promise<TwoFaAssociation> {
  try {
    const response = await apiClient.post<{
      secretCode: string
      session?: string
    }>('/auth/associate-software-token', { accessToken: accessToken() })
    const secretCode = response.data.secretCode
    // Build the otpauth:// URI client-side from the Cognito secret (the QR the
    // authenticator app scans). Mirrors Reenrol.tsx.
    const qrCodeUri = `otpauth://totp/${ISSUER}?secret=${secretCode}&issuer=${ISSUER}`
    return { secretCode, qrCodeUri }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function verifySoftwareToken(
  code: string
): Promise<{ success: boolean }> {
  try {
    const response = await apiClient.post<{ status?: string }>(
      '/auth/verify-software-token',
      {
        accessToken: accessToken(),
        userCode: code,
        friendlyDeviceName: 'Authenticator App'
      }
    )
    // Backend returns { status: "SUCCESS" } on success; a wrong code comes back
    // as a fail envelope. Throw so react-query's onError surfaces it.
    if (response.data?.status !== 'SUCCESS') {
      throw new Error('Invalid verification code')
    }
    return { success: true }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function enableTwoFa(): Promise<{ backupCodes: string[] }> {
  try {
    const response = await apiClient.post<{ backupCodes?: string[] }>(
      '/auth/2fa/enable',
      // BE SetMfaPreferenceRequest requires { accessToken, enabled }.
      { accessToken: accessToken(), enabled: true }
    )
    return { backupCodes: response.data?.backupCodes ?? [] }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function disableTwoFa(): Promise<void> {
  try {
    await apiClient.post('/auth/2fa/disable', {
      accessToken: accessToken(),
      enabled: false
    })
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchTwoFaStatus(): Promise<TwoFaStatus> {
  try {
    const response = await apiClient.post<{
      mfaEnabled?: boolean
      legacyOtpEnabled?: boolean
      cognitoMfaEnrolledAt?: string | null
    }>('/auth/2fa/status', { accessToken: accessToken() })
    return {
      enrolled: response.data?.mfaEnabled ?? false,
      legacyOtpEnabled: response.data?.legacyOtpEnabled ?? false,
      enrolledAt: response.data?.cognitoMfaEnrolledAt ?? undefined
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
